import { Layout } from "providers/Layout";
import { LayoutHeader } from "./components/LayoutHeader";
import { LayoutFooter } from "./components/LayoutFooter";
import { InitialConfigurationSlice } from "./slices/InitialConfigurationSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Show } from "shared/components";
import { SplitByOptionSlice } from "./slices/SplitByOptionSlice";
import { PropertyGroupSplitBy, PropertyGroupStep } from "./types";
import { useState, useCallback, useMemo, useEffect } from "react";
import { Loader } from "shared/components/Loader";
import { ConfirmGroupsSlice } from "./slices/ConfirmGroupsSlice.tsx";
import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { getNextStep, getPreviousStep } from "./utils";
import { PROPERTY_GROUPS_QUERY_KEY } from "views/property-groups/constants";
import { useNavigationBlocker } from "shared/hooks/use-navigation-blocker";
import { LeaveWarningModal } from "shared/components/LeaveWarningModal";
import { useSegment } from "./hooks/use-segment";
import { PropertyGroup as PropertyGroupCamelCase } from "./helpers/group_tree";
import { SpotlightProvider } from "./providers/SpotlightProvider";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useAuthContext } from "providers/Auth";

export const PropertyGroupsConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const enableOwnerSelection = useFeatureFlagEnabled(
    "enable-owner-property-group"
  );
  const { type } = useAuthContext();
  const currentStep = (new URLSearchParams(location.search).get("step") ||
    PropertyGroupStep.INITIAL_CONFIGURATION) as PropertyGroupStep;

  const isEditMode =
    new URLSearchParams(location.search).get("edit") === "true";

  const [selectedSplitOptions, setSelectedSplitOptions] = useState<
    PropertyGroupSplitBy[]
  >([]);
  const [selectedTemplates, setSelectedTemplates] = useState<
    {
      id: string;
      verification_template_id: string;
    }[]
  >([]);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const customBlocker = useCallback(
    (targetUrl: string) => {
      if (targetUrl.includes("/property-groups/configuration")) return false;

      return (
        currentStep === PropertyGroupStep.CONFIRM_GROUPS ||
        !targetUrl.includes("step=")
      );
    },
    [currentStep]
  );

  const { warningModalOpen, onLeave, onCancel } = useNavigationBlocker(
    true,
    "Changes you made will not be saved",
    customBlocker
  );

  const supportedSplitByOptions = useMemo(() => {
    const options: PropertyGroupSplitBy[] = [];

    if (enableOwnerSelection) options.push(PropertyGroupSplitBy.OWNER);

    if (type.isPropertyGroupMarket)
      options.push(
        PropertyGroupSplitBy.MARKET,
        PropertyGroupSplitBy.CITY,
        PropertyGroupSplitBy.ZIP
      );
    else
      options.push(
        PropertyGroupSplitBy.STATE,
        PropertyGroupSplitBy.CITY,
        PropertyGroupSplitBy.ZIP
      );

    return options;
  }, [type.isPropertyGroupMarket, enableOwnerSelection]);

  const {
    data: existingPropertyGroups,
    isLoading: isLoadingExistingPropertyGroups
  } = useQuery({
    queryKey: [PROPERTY_GROUPS_QUERY_KEY.PROPERTY_GROUPS, isEditMode],
    queryFn: () =>
      instance
        .get("/portal/v1/property_groups")
        .then((res) => res.data.property_groups),
    enabled: isEditMode
  });

  const {
    mutate: confirmPropertyGroups,
    isPending: isConfirmingPropertyGroups
  } = useMutation({
    mutationFn: (groups: PropertyGroupCamelCase[]) => {
      return instance.post("/portal/v1/property_groups/build", {
        // We might need this in the future — currently defaulted to true
        use_property_group_verification_template: true,
        group_data: groups.map((group) => {
          const groupName = group.titleChain[group.titleChain.length - 1];

          const verificationTemplateId =
            selectedTemplates.find((template) => template.id === group.identity)
              ?.verification_template_id || null;

          const splitDataChain = group.splitDataChain.map((item) => ({
            split_type: item.splitType,
            split_values: item.splitValues,
            is_other: item.isOther
          }));

          return {
            name: groupName,
            verification_template_identifier: verificationTemplateId,
            title_chain: group.titleChain,
            split_data_chain: splitDataChain
          };
        })
      });
    },
    onSuccess: () => {
      setIsConfirmed(true);
      toast.success("Property groups have been created successfully");
      navigate("/property-groups");
      onLeave();
    },
    onError: (error) => {
      toast.error(
        `Failed to update verification templates: ${getErrorMessage(error)}`
      );
    }
  });

  const handlePreviousStep = () => {
    if (currentStep === PropertyGroupStep.INITIAL_CONFIGURATION) {
      navigate("/property-groups");
      return;
    }

    const previousStep = getPreviousStep(selectedSplitOptions, currentStep);
    let targetPath = `/property-groups/configuration?step=${previousStep}`;
    const isFirstStep =
      currentStep === (selectedSplitOptions[0] as unknown as PropertyGroupStep);

    if (isEditMode)
      if (isFirstStep) targetPath = "/property-groups";
      else targetPath += "&edit=true";
    else if (isFirstStep) targetPath = "/property-groups/configuration";

    navigate(targetPath);
  };

  const handleNextStep = () => {
    if (currentStep === PropertyGroupStep.INITIAL_CONFIGURATION)
      createPropertyGroupConfig();
    else if (currentStep === PropertyGroupStep.CONFIRM_GROUPS)
      confirmPropertyGroups(getGroups());
    else {
      const nextStep = getNextStep(currentStep, selectedSplitOptions);
      navigate(
        `/property-groups/configuration?step=${nextStep}${isEditMode ? "&edit=true" : ""}`
      );
    }
  };

  const { mutate: createPropertyGroupConfig } = useMutation({
    mutationFn: () =>
      instance.post("/portal/v1/property_groups/config", {
        split_by: selectedSplitOptions
      }),
    onSuccess: () => {
      const nextStep = getNextStep(currentStep, selectedSplitOptions);
      navigate(`/property-groups/configuration?step=${nextStep}`);
    },
    onError: (error) => {
      toast.error(
        `Failed to create property groups config: ${getErrorMessage(error)}`
      );
    }
  });

  const {
    segments,
    isLoading: isLoadingSegments,
    updateGroupTree,
    getGroups
  } = useSegment(selectedSplitOptions, existingPropertyGroups);

  const { data: propertyGroupConfig, isLoading: isPropertyGroupConfigLoading } =
    useQuery({
      queryKey: [PROPERTY_GROUPS_QUERY_KEY.CONFIG],
      queryFn: () =>
        instance
          .get("/portal/v1/property_groups/config")
          .then((res) => res.data)
    });

  useEffect(() => {
    if (propertyGroupConfig)
      setSelectedSplitOptions(
        propertyGroupConfig.split_by.filter((option: PropertyGroupSplitBy) =>
          supportedSplitByOptions.includes(option)
        )
      );
  }, [propertyGroupConfig, supportedSplitByOptions]);

  if (isPropertyGroupConfigLoading || isLoadingExistingPropertyGroups)
    return (
      <div className="flex justify-center items-center h-[100vh]">
        <Loader />
      </div>
    );

  return (
    <SpotlightProvider
      currentStep={currentStep}
      selectedSplitOptions={
        selectedSplitOptions as unknown[] as PropertyGroupStep[]
      }
    >
      <Layout
        headerClassName="px-6 py-[22px] border-b border-alpha-100"
        footerClassName="px-6 py-4 border-t border-alpha-100"
        header={
          <LayoutHeader
            isEditMode={isEditMode}
            currentStep={currentStep}
            selectedSplitOptions={selectedSplitOptions}
          />
        }
        footer={
          <LayoutFooter
            handlePreviousStep={handlePreviousStep}
            isContinueButtonDisabled={
              currentStep === PropertyGroupStep.INITIAL_CONFIGURATION &&
              selectedSplitOptions.length === 0
            }
            handleNextStep={handleNextStep}
            currentStep={currentStep}
            isButtonLoading={isConfirmingPropertyGroups}
          />
        }
      >
        <Show if={warningModalOpen && !isConfirmed}>
          <LeaveWarningModal
            onLeave={onLeave}
            open={warningModalOpen}
            onClose={onCancel}
          />
        </Show>

        {[
          {
            id: PropertyGroupStep.INITIAL_CONFIGURATION,
            view: [PropertyGroupStep.INITIAL_CONFIGURATION],
            slice: (
              <InitialConfigurationSlice
                supportedSplitByOptions={supportedSplitByOptions}
                selectedSplitOptions={selectedSplitOptions}
                setSelectedSplitOptions={setSelectedSplitOptions}
              />
            )
          },
          {
            id: "split-by-option",
            view: [
              PropertyGroupStep.OWNER,
              PropertyGroupStep.STATE,
              PropertyGroupStep.CITY,
              PropertyGroupStep.ZIP,
              PropertyGroupStep.MARKET
            ],
            slice: (
              <SplitByOptionSlice
                isLoadingSegments={isLoadingSegments}
                handleUpdateSegments={(data) =>
                  updateGroupTree(data.nodeId, data.splitDatas)
                }
                segments={segments}
                currentStep={currentStep as unknown as PropertyGroupSplitBy}
                selectedSplitOptions={selectedSplitOptions}
              />
            )
          },
          {
            id: PropertyGroupStep.CONFIRM_GROUPS,
            view: [PropertyGroupStep.CONFIRM_GROUPS],
            slice: (
              <ConfirmGroupsSlice
                selectedTemplates={selectedTemplates}
                setSelectedTemplates={setSelectedTemplates}
                selectedSplitOptions={selectedSplitOptions}
                groups={getGroups()}
              />
            )
          }
        ].map(({ id, view, slice }) => (
          <Show key={id} if={view.includes(currentStep)}>
            {/* Key is required to show feedback that a page changed. */}
            <div key={id + currentStep} className="animate-appear">
              {slice}
            </div>
          </Show>
        ))}
      </Layout>
    </SpotlightProvider>
  );
};
